import React, { useState, useEffect } from 'react';
import { 
    TrendingUp, Package, MapPin, Waypoints, 
    Clock, BarChart3 
} from 'lucide-react';
import { 
    XAxis, YAxis, CartesianGrid, Tooltip, 
    ResponsiveContainer, AreaChart, Area 
} from 'recharts';
import api from '../../api/axios';
import { echo } from '../../utils/echo';

const DashboardPage = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    // --- FUNCIÓN PARA DAR COLOR AL ESTADO DINÁMICO ---
    const getStatusStyles = (status) => {
        const s = status?.toLowerCase();
        switch (s) {
            case 'entregado':
            case 'completado':
                return 'bg-green-100 text-green-700 dark:bg-green-500/20';
            case 'en tránsito':
            case 'en_transito':
            case 'en camino':
                return 'bg-blue-100 text-blue-700 dark:bg-blue-500/20';
            case 'recolectado':
            case 'recibido':
                return 'bg-orange-100 text-orange-700 dark:bg-orange-500/20';
            case 'pendiente':
                return 'bg-gray-100 text-gray-600 dark:bg-white/10';
            default:
                return 'bg-red-50 text-red-600 dark:bg-red-500/10'; // Por si hay algún error o cancelación
        }
    };
    
    const [verFinanzas, setVerFinanzas] = useState(false);

    const loadStats = async () => {
        try {
            const res = await api.get('/dashboard/stats');
            setStats(res.data);
        } catch (err) {
            console.error("Error al cargar estadísticas:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadStats();

        const channel = echo.channel('shipments');
        const handleStatusChange = (e) => {
            console.log("⚡ [Pusher] Actualización de métricas en vivo:", e);
            loadStats();
        };

        channel.listen('.status.changed', handleStatusChange);
        channel.listen('status.changed', handleStatusChange);
        channel.listen('ShipmentStatusChanged', handleStatusChange);

        return () => {
            channel.stopListening('.status.changed');
            channel.stopListening('status.changed');
            channel.stopListening('ShipmentStatusChanged');
        };
    }, []);

    if (loading) return (
        <div className="h-screen flex items-center justify-center">
            <div className="p-10 font-black animate-pulse text-[#CE010B] tracking-widest uppercase text-center">
                Cargando métricas Más Envíos...
            </div>
        </div>
    );

    return (
        <div className="space-y-8 animate-in fade-in duration-700 pb-10">
            {/* SALUDO Y CABECERA */}
            <div>
                <h2 className="text-4xl font-black uppercase tracking-tighter text-black dark:text-white">
                    Panel de Control <span className="text-[#CE010B]">Más Envíos</span>
                </h2>
                <p className="text-sm font-bold text-gray-400 uppercase italic">Resumen operativo de la red logística</p>
            </div>

            {/* KPI CARDS */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div 
                onClick={() => setVerFinanzas(!verFinanzas)} 
                className="cursor-pointer transition-all hover:opacity-80 active:scale-95"
                >
                <StatCard 
                    icon={<TrendingUp />} 
                    // Usamos ?. para evitar errores si stats aún es null
                    label={verFinanzas ? "Ingresos Totales" : "Finanzas (Clic para ver)"} 
                    value={verFinanzas ? `$${stats?.kpis?.revenue || '0.00'}` : "$ ••••"} 
                    color="bg-green-500" 
                />
                </div>
                <StatCard icon={<Package />} label="Envíos Realizados" value={stats?.kpis?.shipments || 0} color="bg-blue-500" />
                <StatCard icon={<MapPin />} label="Sedes Activas" value={stats?.kpis?.branches || 0} color="bg-[#CE010B]" />
                <StatCard icon={<Waypoints />} label="Rutas Conectadas" value={stats?.kpis?.routes || 0} color="bg-orange-500" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* GRÁFICA DE VOLUMEN (7 DÍAS) */}
                <div className="lg:col-span-2 bg-white dark:bg-black/20 p-8 rounded-[2.5rem] border border-black/5 shadow-sm">
                    <div className="flex justify-between items-center mb-8">
                        <h3 className="font-black uppercase text-xs tracking-widest flex items-center gap-2 text-gray-500">
                            <BarChart3 size={18} className="text-[#CE010B]"/> Flujo de Envíos (Últimos 7 días)
                        </h3>
                    </div>
                    <div className="h-[300px] w-full min-w-0">
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={stats?.chart || []}>
                                <defs>
                                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#CE010B" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#CE010B" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                                <XAxis 
                                    dataKey="date" 
                                    axisLine={false} 
                                    tickLine={false} 
                                    tick={{fontSize: 10, fontWeight: 'bold'}} 
                                />
                                <YAxis 
                                    axisLine={false} 
                                    tickLine={false} 
                                    tick={{fontSize: 10, fontWeight: 'bold'}} 
                                />
                                <Tooltip 
                                    contentStyle={{borderRadius: '15px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)'}} 
                                />
                                <Area 
                                    type="monotone" 
                                    dataKey="count" 
                                    stroke="#CE010B" 
                                    strokeWidth={4} 
                                    fillOpacity={1} 
                                    fill="url(#colorCount)" 
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* LISTADO DE ÚLTIMOS ENVÍOS DINÁMICO */}
                <div className="bg-white dark:bg-black/20 p-8 rounded-[2.5rem] border border-black/5 shadow-sm flex flex-col">
                    <h3 className="font-black uppercase text-xs tracking-widest mb-6 flex items-center gap-2 text-gray-500">
                        <Clock size={18} className="text-[#CE010B]"/> Envíos Recientes
                    </h3>
                    
                    <div className="space-y-6 flex-grow">
                        {stats?.recent && stats.recent.length > 0 ? (
                            stats.recent.map((ship) => (
                                <div key={ship.id} className="flex items-center justify-between group py-1">
                                    <div className="flex flex-col">
                                        <span className="text-[10px] font-black text-[#CE010B] uppercase tracking-tighter">
                                            #{ship.tracking_number}
                                        </span>
                                        <span className="text-xs font-bold text-gray-700 dark:text-gray-300 truncate max-w-[150px]">
                                            {ship.origin_branch?.name || 'Origen'} → {ship.destination_branch?.name || 'Destino'}
                                        </span>
                                    </div>
                                    <div className="text-right flex flex-col items-end gap-1">
                                        <span className="text-xs font-black block text-black dark:text-white">
                                            ${Number(ship.total_price).toFixed(2)}
                                        </span>
                                        {/* ESTADO DINÁMICO DESDE SHIPMENTS */}
                                        <span className={`text-[8px] px-2 py-0.5 rounded-md font-black uppercase tracking-tighter ${getStatusStyles(ship.status)}`}>
                                            {ship.status || 'Pendiente'}
                                        </span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-10 text-gray-400 font-bold text-xs uppercase italic">
                                No hay envíos registrados
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

/* Componente Interno para las Tarjetas KPI */
const StatCard = ({ icon, label, value, color }) => (
    <div className="bg-white dark:bg-black/20 p-6 rounded-[2.2rem] border border-black/5 flex items-center gap-5 hover:-translate-y-1 transition-all shadow-sm">
        <div className={`${color} p-4 rounded-2xl text-white shadow-lg shadow-current/20`}>
            {React.cloneElement(icon, { size: 24 })}
        </div>
        <div>
            <p className="text-[10px] font-black uppercase text-gray-400 tracking-widest">{label}</p>
            <p className="text-2xl font-black tracking-tighter text-black dark:text-white">{value}</p>
        </div>
    </div>
);

export default DashboardPage;